import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  NJAvatarRoot,
  NJBadge,
  NJButton,
  NJIcon,
  NJSidebarDivider,
  NJSidebarGroup,
  NJSidebarHeader,
  NJSidebarItem,
  NJSidebarRoot,
} from "@engie-group/fluid-design-system-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "@/api/useCurrentUser";
import { useAccessRequests } from "@/features/access/useAccessRequests";
import { usePendingSkillCount } from "@/features/admin/useAdminSkills";
import {
  clearImpersonation,
  useImpersonatedUserId,
} from "@/features/auth/impersonation";
import { ChatComposer } from "@/features/chat/ChatComposer";
import { ChatProvider } from "@/features/chat/ChatProvider";
import { userDisplayName } from "@/features/auth/userDisplayName";
import { SessionSidebar } from "@/features/sessions/SessionSidebar";
import { ThemeToggle } from "@/features/theme/ThemeToggle";
import { useMediaQuery } from "./useMediaQuery";

/** True when the current user holds the `app-admin` group. */
function useIsAdmin(): boolean {
  const { data: user } = useCurrentUser();
  return (user?.groups ?? []).includes("app-admin");
}

// Legacy AgentPlaybookSidebar collapses to an off-canvas drawer under this width.
const MOBILE_BREAKPOINT_PX = 1100;

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();

  const groups = user?.groups ?? [];
  const isValidator = groups.some((group) =>
    /^agent-.+-validators$/.test(group),
  );
  const isAdmin = groups.includes("app-admin");

  const { data: accessRequests } = useAccessRequests("PENDING");
  const pendingRequestCount = isValidator ? (accessRequests?.length ?? 0) : 0;

  const { data: pendingSkills } = usePendingSkillCount(isAdmin);
  const pendingSkillCount = pendingSkills ?? 0;

  const go = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const isActive = (path: string) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  return (
    <>
      <NJSidebarGroup>
        <NJSidebarItem
          selected={isActive("/")}
          leading={<NJIcon name="home" />}
          onClick={() => go("/")}
        >
          Home
        </NJSidebarItem>
        <NJSidebarItem
          selected={isActive("/agents")}
          leading={<NJIcon name="store" />}
          onClick={() => go("/agents")}
        >
          Agent Lib
        </NJSidebarItem>
        <NJSidebarItem
          selected={isActive("/skills")}
          leading={<NJIcon name="inventory_2" />}
          onClick={() => go("/skills")}
        >
          Skill Store
        </NJSidebarItem>
        {!isAdmin && (
          <NJSidebarItem
            selected={isActive("/chat")}
            leading={<NJIcon name="chat" />}
            onClick={() => go("/chat")}
          >
            Chat
          </NJSidebarItem>
        )}
        {isValidator && (
          <NJSidebarItem
            selected={isActive("/access-requests")}
            leading={<NJIcon name="how_to_reg" />}
            onClick={() => go("/access-requests")}
          >
            <span className="flex w-full items-center justify-between">
              <span>Access requests</span>
              {pendingRequestCount > 0 && (
                <NJBadge>{pendingRequestCount}</NJBadge>
              )}
            </span>
          </NJSidebarItem>
        )}
      </NJSidebarGroup>

      {isAdmin && (
        <>
          <NJSidebarDivider />
          <NJSidebarGroup>
            <p className="px-4 pb-2 pt-1 text-xs font-nj-bold uppercase text-nj-neutral-tertiary-default">
              Administration
            </p>
            <NJSidebarItem
              selected={isActive("/admin/users")}
              leading={<NJIcon name="group" />}
              onClick={() => go("/admin/users")}
            >
              Users
            </NJSidebarItem>
            <NJSidebarItem
              selected={isActive("/admin/groups")}
              leading={<NJIcon name="groups" />}
              onClick={() => go("/admin/groups")}
            >
              Groups
            </NJSidebarItem>
            <NJSidebarItem
              selected={isActive("/admin/agents")}
              leading={<NJIcon name="smart_toy" />}
              onClick={() => go("/admin/agents")}
            >
              Agents
            </NJSidebarItem>
            <NJSidebarItem
              selected={isActive("/admin/skills")}
              leading={<NJIcon name="category" />}
              onClick={() => go("/admin/skills")}
            >
              <span className="flex w-full items-center justify-between">
                <span>Skills</span>
                {pendingSkillCount > 0 && (
                  <NJBadge variant="warning">{pendingSkillCount}</NJBadge>
                )}
              </span>
            </NJSidebarItem>
            <NJSidebarItem
              selected={isActive("/admin/skill-categories")}
              leading={<NJIcon name="sell" />}
              onClick={() => go("/admin/skill-categories")}
            >
              Skill categories
            </NJSidebarItem>
            <NJSidebarItem
              selected={isActive("/admin/use-cases")}
              leading={<NJIcon name="label" />}
              onClick={() => go("/admin/use-cases")}
            >
              Agent use cases
            </NJSidebarItem>
          </NJSidebarGroup>
        </>
      )}
    </>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const displayName = userDisplayName(user);
  const isAdmin = useIsAdmin();

  return (
    <NJSidebarRoot className="!flex !h-full !w-full !flex-shrink-0 !flex-col">
      <NJSidebarHeader
        logo={
          <div className="flex w-full justify-center">
            <img
              alt="ENGIE"
              className="h-32"
              src="https://assets.design.digital.engie.com/brand/logo-engie-blue.svg"
            />
          </div>
        }
        logoCollapsed={
          <div className="flex w-full justify-center">
            <img
              alt="ENGIE"
              className="h-8"
              src="https://www.engie.design/assets/brand/logo-engie-small-blue.svg"
            />
          </div>
        }
      />

      {!isAdmin && (
        <div className="px-3 pb-2 pt-1">
          <NJButton
            className="w-full"
            label="New session"
            icon="add_circle_outline"
            variant="ai"
            emphasis="subtle"
            onClick={() => {
              navigate("/chat");
              onNavigate?.();
            }}
          />
        </div>
      )}

      <NavItems onNavigate={onNavigate} />

      {!isAdmin && (
        <>
          <NJSidebarDivider />
          <SessionSidebar onNavigate={onNavigate} />
        </>
      )}

      <NJSidebarDivider className="!mt-auto" />
      <NJSidebarGroup>
        <div className="px-6 pb-2">
          <ThemeToggle />
        </div>
        <NJSidebarItem
          leading={
            <span className="flex flex-shrink-0">
              <NJAvatarRoot
                label={displayName}
                initials={initialsFromName(displayName)}
                scale="sm"
              />
            </span>
          }
        >
          <span className="truncate">{displayName}</span>
        </NJSidebarItem>
      </NJSidebarGroup>
    </NJSidebarRoot>
  );
}

/** Shared app layout: ENGIE Fluid sidebar (desktop) / off-canvas drawer (mobile, <1100px),
 * matching the legacy `AgentPlaybookSidebar` navigation structure, laid out with Tailwind. */
export function AppShell({ children }: { children: ReactNode }) {
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isAdmin = useIsAdmin();
  const impersonatedUserId = useImpersonatedUserId();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [isMobile, isSidebarOpen]);

  return (
    <div
      className="flex h-dvh max-h-dvh w-full overflow-hidden bg-nj-neutral-primary-default"
      style={{
        backgroundImage:
          "radial-gradient(circle at 12% 18%, rgba(0, 112, 154, 0.1), transparent 36%), radial-gradient(circle at 86% 82%, rgba(255, 191, 0, 0.14), transparent 28%)",
      }}
    >
      {!isMobile && (
        <aside className="w-[280px] flex-shrink-0">
          <SidebarContent />
        </aside>
      )}

      {isMobile && (
        <>
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={() => setIsSidebarOpen(false)}
            className={`fixed inset-0 z-20 bg-black/40 transition-opacity ${
              isSidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          />
          <aside
            aria-hidden={!isSidebarOpen}
            className={`fixed inset-y-0 left-0 z-30 w-[280px] transform bg-nj-neutral-primary-default shadow-lg transition-transform duration-200 ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <SidebarContent onNavigate={() => setIsSidebarOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex flex-shrink-0 items-center gap-3 border-b border-nj-neutral-subtle-default bg-nj-neutral-primary-default px-4 py-3 sm:px-6">
          {isMobile && (
            <NJButton
              label="Menu"
              icon="menu"
              variant="secondary"
              emphasis="subtle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
            />
          )}
          <strong className="text-base">R&amp;FGBU Agent Desk</strong>
          <div className="ml-auto flex items-center gap-2">
            {impersonatedUserId && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[#f3d38a] bg-[#fff7e3] px-3 py-1 text-xs font-nj-bold text-[#8a5a00]">
                Impersonating
                <button
                  type="button"
                  className="underline"
                  onClick={() => {
                    clearImpersonation();
                    void queryClient.invalidateQueries();
                  }}
                >
                  Stop
                </button>
              </span>
            )}
            {isAdmin && (
              <span className="rounded-full border border-[#f3d38a] bg-[#fff7e3] px-3 py-1 text-xs font-nj-bold text-[#8a5a00]">
                Admin mode - chat disabled
              </span>
            )}
          </div>
        </header>

        <ChatProvider>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {children}
          </div>
          {!isAdmin && <ChatComposer />}
        </ChatProvider>
      </main>
    </div>
  );
}
